import { useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { ChevronLeft, ChevronRight, Download } from 'lucide-react';
import ReportPageScaffold from '../ReportPageScaffold';
import { BlockLoader } from '../loading';
import { useTableSort, SortTh, compareSortText } from '../dashboard/useTableSort';
import { usePagination } from '../../hooks/usePagination';
import { buildCsv, downloadCsv, CSV_UTF8_BOM } from '../../utils/csv';
import { storeVisitTargetsAPI } from '../../services/api';
import { useAuthStore } from '../../stores/authStore';
import { useHeldPage } from '../../navigation/useHeldPage';
import VisitsModal, { type VisitRow } from './VisitsModal';
import type { CompliancePerson, ComplianceStatus, ComplianceVisitsResponse, DayOff, VisitComplianceResponse } from './types';

/**
 * Who met their store visit target: the "Compliance" tab of Reports › Store Visit
 * Compliance. One week at a time (Monday to Sunday, India time), one row per person
 * who holds a role with visits switched on in Master Data › Store Visits Configuration.
 *
 * Every verdict comes from the server: what counts as a visit, each day's target, whether
 * a day counted, which days were leave, a week off or a holiday, how many days were
 * required, and whether the week is met, short, still open or excused. This tab only
 * draws the answer, so it can never disagree with the server about the same week.
 *
 * While a week runs only the days that are over are judged: today shows its visits so far
 * in grey, the days still to come are shaded, and a person is Met, Short (can no longer
 * make it) or "N more days".
 *
 * A day's number opens the visits it counts, in the Store Visits report's pop-up; Days met
 * opens the whole week's. Only the Visits list is shown there: the report's Pending stores
 * tab is built from its handled-store lists, which this report does not read.
 */

const PAGE_SIZE = 50;

const WEEKDAYS = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];
const MONTHS = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];

// Days travel as YYYY-MM-DD and are only ever read in UTC, so the browser's own time
// zone cannot move one to the day before or after.
const utcOf = (day: string): Date => {
  const [year, month, date] = day.split('-').map(Number);
  return new Date(Date.UTC(year, month - 1, date));
};
const shiftDay = (day: string, by: number): string => {
  const d = utcOf(day);
  d.setUTCDate(d.getUTCDate() + by);
  return d.toISOString().slice(0, 10);
};
const mondayOf = (day: string): string => shiftDay(day, -((utcOf(day).getUTCDay() + 6) % 7));

/** "5 – 11 Oct 2026"; the month or the year is repeated only when the week crosses one. */
const weekLabel = (start: string): string => {
  const a = utcOf(start);
  const b = utcOf(shiftDay(start, 6));
  const tail = `${b.getUTCDate()} ${MONTHS[b.getUTCMonth()]} ${b.getUTCFullYear()}`;
  if (a.getUTCFullYear() !== b.getUTCFullYear()) {
    return `${a.getUTCDate()} ${MONTHS[a.getUTCMonth()]} ${a.getUTCFullYear()} – ${tail}`;
  }
  if (a.getUTCMonth() !== b.getUTCMonth()) return `${a.getUTCDate()} ${MONTHS[a.getUTCMonth()]} – ${tail}`;
  return `${a.getUTCDate()} – ${tail}`;
};

/** Column heading: "Mon 5". */
const dayHead = (day: string): string => {
  const d = utcOf(day);
  return `${WEEKDAYS[d.getUTCDay()]} ${d.getUTCDate()}`;
};
/** In the pop-up and its buttons: "Thu 15 Oct". */
const dayLong = (day: string): string => {
  const d = utcOf(day);
  return `${WEEKDAYS[d.getUTCDay()]} ${d.getUTCDate()} ${MONTHS[d.getUTCMonth()]}`;
};
/** "12 – 18 Oct", "28 Sep – 4 Oct"; the year only when the week crosses one. */
const shortWeekLabel = (start: string): string => {
  const a = utcOf(start);
  if (a.getUTCFullYear() !== utcOf(shiftDay(start, 6)).getUTCFullYear()) return weekLabel(start);
  return weekLabel(start).replace(/ \d{4}$/, '');
};
/** Download heading: "Mon 05 Oct". A sheet is read away from the week picker, so it carries the month. */
const csvDayHead = (day: string): string => {
  const d = utcOf(day);
  return `${WEEKDAYS[d.getUTCDay()]} ${String(d.getUTCDate()).padStart(2, '0')} ${MONTHS[d.getUTCMonth()]}`;
};

// `rank` is the order people are listed in: those who fell short come first, and those
// of whom nothing was required last.
/** What the page shows: the server's three, plus Excused for a week of which nothing was required. */
type ShownStatus = ComplianceStatus | 'excused';
const STATUS: Record<ShownStatus, { label: string; chip: string; rank: number }> = {
  short: { label: 'Short', chip: 'bg-red-50 text-red-700', rank: 0 },
  // Labelled per person: "N more days" (see statusLabel).
  in_progress: { label: 'More days', chip: 'bg-gray-100 text-gray-600', rank: 1 },
  met: { label: 'Met', chip: 'bg-green-50 text-green-700', rank: 2 },
  excused: { label: 'Excused', chip: 'bg-gray-50 text-gray-500 ring-1 ring-inset ring-gray-200', rank: 3 },
};

// A day off with no visit shows a plain grey letter, never a coloured pill: amber
// already means "below target" here.
const OFF: Record<NonNullable<DayOff>, { mark: string; title: string }> = {
  leave: { mark: 'L', title: 'Leave' },
  week_off: { mark: 'WO', title: 'Week off' },
  holiday: { mark: 'H', title: 'Holiday' },
};
const OFF_MARK = 'text-xs font-medium text-gray-500';

const DAY_PILL = 'inline-block min-w-[1.75rem] rounded-full px-2 py-0.5 text-xs font-semibold tabular-nums';
const MET_PILL = 'bg-green-50 text-green-700';
const BELOW_PILL = 'bg-amber-50 text-amber-700';
// Today, and a day with no target: the visits, judged by nothing.
const PLAIN_PILL = 'bg-gray-100 text-gray-600';
// A count that opens its visits: the pill as it is, plus a hover and a keyboard ring.
const PILL_BUTTON = 'cursor-pointer transition hover:underline focus:outline-none focus-visible:ring-2 focus-visible:ring-primary-500 focus-visible:ring-offset-1';
// A day of the running week still to come: shaded, so a blank Saturday is not read as leave.
const UPCOMING_CELL = 'bg-gray-100/70';
const UPCOMING_HINT = 'Still to come';
const STEP_BUTTON =
  'rounded-md border border-gray-300 bg-white px-2 py-2.5 text-gray-600 hover:bg-gray-50 disabled:cursor-not-allowed disabled:opacity-50';

// When the roster could not be read the server may have no name to send; the code still identifies the person.
const nameOf = (p: CompliancePerson): string => p.employeeName || p.employeeCode;

// A server from before leave was read sends neither field. Its week then reads as it
// always did: every day an ordinary one, and the role's own target required.
const offOn = (p: CompliancePerson, i: number): { mark: string; title: string } | null => {
  const kind = p.offByDay?.[i];
  return kind ? OFF[kind] ?? null : null;
};
const requiredOf = (p: CompliancePerson): number => p.daysRequired ?? p.daysPerWeek;
// The server sends such a week as 'met' (a value every open browser tab knows), so the
// page tells it apart by the 0. A value this page does not know (a newer server) is
// shown as still open rather than breaking the page.
const statusOf = (p: CompliancePerson): ShownStatus =>
  (requiredOf(p) === 0 ? 'excused' : p.status in STATUS ? p.status : 'in_progress');
/** The chip's words: a week still open says how many more days it needs. */
const statusLabel = (p: CompliancePerson): string => {
  const status = statusOf(p);
  if (status !== 'in_progress') return STATUS[status].label;
  const more = Math.max(1, requiredOf(p) - p.daysMet);
  return `${more} more ${more === 1 ? 'day' : 'days'}`;
};
// Each day's own stores-a-day target; null on a day with none. A server from before
// targets had a valid-from date sends one number for the week.
const targetOn = (p: CompliancePerson, i: number): number | null =>
  (p.storesPerDayByDay ? p.storesPerDayByDay[i] ?? null : p.storesPerDay);
/** The week's stores-a-day targets in order, each change once: [2], or [2, 3] when it changed that week. */
const storesTargets = (p: CompliancePerson): number[] => {
  const values: number[] = [];
  for (let i = 0; i < 7; i += 1) {
    const n = targetOn(p, i);
    if (n !== null && values[values.length - 1] !== n) values.push(n);
  }
  if (values.length === 0) values.push(p.storesPerDay);
  return values;
};
/** The numbers as the screen writes them: "2", or "2 → 3". The download's column holds the same. */
const storesFigures = (p: CompliancePerson): string => storesTargets(p).join(' → ');
/** Under the role: "2 stores a day", or "2 → 3 stores a day" when it changed that week. */
const storesLine = (p: CompliancePerson): string => {
  const values = storesTargets(p);
  return `${storesFigures(p)} ${values.length === 1 && values[0] === 1 ? 'store' : 'stores'} a day`;
};
/** "Target 5, less 2 excused" on hover, only for someone of whom less than the target was required. */
const lessRequiredTitle = (p: CompliancePerson): string | undefined => {
  const less = p.daysPerWeek - requiredOf(p);
  return less > 0 ? `Target ${p.daysPerWeek}, less ${less} excused` : undefined;
};

const messageOf = (err: unknown, fallback: string): string => {
  const message = (err as { response?: { data?: { message?: unknown } } })?.response?.data?.message;
  return typeof message === 'string' && message.trim() ? message : fallback;
};

export default function VisitComplianceTab() {
  const projectId = useAuthStore((s) => s.selectedProjectId);
  // The page where targets are set, when this person holds it: only then is the way there offered.
  const configPage = useHeldPage('/master-management/store-visit-forms');

  // The Monday asked for. Undefined on arrival: the server then answers with this week.
  const [weekStart, setWeekStart] = useState<string | undefined>(undefined);
  const [answer, setAnswer] = useState<VisitComplianceResponse | null>(null);
  // Monday of the week today (India) falls in, kept from the last answer so the Next
  // button still knows where to stop after a week that failed to load.
  const [thisWeek, setThisWeek] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [searchQuery, setSearchQuery] = useState('');
  const [roleFilter, setRoleFilter] = useState('all');
  const [currentPage, setCurrentPage] = useState(1);
  // The visits pop-up: whose, which days (one, or the week), and what came back.
  const [visitsAsk, setVisitsAsk] = useState<{ person: CompliancePerson; from: string; to: string } | null>(null);
  const [visits, setVisits] = useState<VisitRow[]>([]);
  const [visitsLoading, setVisitsLoading] = useState(false);
  const [visitsError, setVisitsError] = useState<string | null>(null);

  useEffect(() => {
    if (!projectId) { setLoading(false); return undefined; }
    // Stepping through weeks quickly must not let a slow earlier answer land on a later week.
    let cancelled = false;
    setLoading(true);
    setError(null);
    storeVisitTargetsAPI.compliance(projectId, weekStart)
      .then((res) => {
        if (cancelled) return;
        const next: VisitComplianceResponse = res.data.data;
        setThisWeek(mondayOf(next.today));
        // A role picked in one week (or project) may not exist in the next.
        setRoleFilter((prev) => (next.people.some((p) => p.roleId === prev) ? prev : 'all'));
        setAnswer(next);
      })
      .catch((err: unknown) => {
        if (cancelled) return;
        setAnswer(null);
        setError(messageOf(err, 'Could not load the report.'));
      })
      .finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  }, [projectId, weekStart]);

  // Another week or project closes the pop-up: its days are no longer on screen.
  useEffect(() => { setVisitsAsk(null); }, [projectId, weekStart]);

  useEffect(() => {
    if (!visitsAsk || !projectId) return undefined;
    // A second number clicked before the first answer lands must not show the first's visits.
    let cancelled = false;
    setVisits([]);
    setVisitsError(null);
    setVisitsLoading(true);
    storeVisitTargetsAPI.complianceVisits(projectId, visitsAsk.person.employeeCode, visitsAsk.from, visitsAsk.to)
      .then((res) => {
        if (cancelled) return;
        const got: ComplianceVisitsResponse = res.data.data;
        setVisits(got.rows ?? []);
      })
      .catch((err: unknown) => {
        if (!cancelled) setVisitsError(messageOf(err, 'Could not load these visits.'));
      })
      .finally(() => { if (!cancelled) setVisitsLoading(false); });
    return () => { cancelled = true; };
  }, [projectId, visitsAsk]);

  const people = useMemo(() => (answer?.targetsSet ? answer.people : []), [answer]);

  const roleOptions = useMemo(() => {
    const seen = new Map<string, string>();
    for (const p of people) if (!seen.has(p.roleId)) seen.set(p.roleId, p.roleName);
    return [...seen.entries()].map(([value, label]) => ({ value, label })).sort((a, b) => compareSortText(a.label, b.label));
  }, [people]);

  // Filtered, and already in the order the table opens in: short first, then still in
  // progress, then met, then excused, by name within each. Clearing a column sort comes
  // back to this.
  const listed = useMemo(() => {
    const q = searchQuery.toLowerCase().trim();
    return people
      .filter((p) => roleFilter === 'all' || p.roleId === roleFilter)
      .filter((p) => !q || nameOf(p).toLowerCase().includes(q) || p.employeeCode.toLowerCase().includes(q))
      .sort((a, b) =>
        STATUS[statusOf(a)].rank - STATUS[statusOf(b)].rank
        || compareSortText(nameOf(a), nameOf(b))
        || compareSortText(a.employeeCode, b.employeeCode));
  }, [people, roleFilter, searchQuery]);

  const sort = useTableSort<CompliancePerson>(listed, {
    person: (p) => nameOf(p),
    role: (p) => p.roleName,
    daysMet: (p) => p.daysMet,
    status: (p) => STATUS[statusOf(p)].rank,
  }, { firstDir: (key) => (key === 'person' || key === 'role' ? 'asc' : 'desc') });

  const { totalPages, safePage, paginatedData } = usePagination(sort.sorted, currentPage, PAGE_SIZE);
  useEffect(() => { setCurrentPage(1); }, [searchQuery, roleFilter, sort.sortKey, sort.sortDir, answer]);

  // The week on screen: the one asked for, else the one the server chose.
  const shownWeek = weekStart ?? answer?.weekStart ?? thisWeek ?? undefined;
  const canGoBack = shownWeek !== undefined;
  const canGoForward = shownWeek !== undefined && thisWeek !== null && shownWeek < thisWeek;

  const handleExport = () => {
    if (!answer) return;
    const headers = [
      'Employee', 'Employee Code', 'Role', 'Target days a week', 'Target stores a day', 'Days required',
      ...answer.days.map(csvDayHead),
      'Days visited', 'Days met', 'Status',
    ];
    const csvRows = sort.sorted.map((p) => [
      // Stores a day as the screen shows it: "2 → 3" when the target changed that week.
      nameOf(p), p.employeeCode, p.roleName, String(p.daysPerWeek), storesFigures(p), String(requiredOf(p)),
      ...answer.days.map((day, i) => {
        const stores = p.storesByDay[i] ?? 0;
        const upcoming = day > answer.today;
        if (stores > 0 && !upcoming) return String(stores);
        // Otherwise the day off, as on screen. A day that has not come yet is left
        // blank: 0 would read as "no visit".
        return offOn(p, i)?.mark ?? (upcoming ? '' : '0');
      }),
      String(p.daysVisited), String(p.daysMet), statusLabel(p),
    ]);
    // With the UTF-8 mark, so Excel shows the arrow of a target that changed as it is.
    downloadCsv(`Store_visit_compliance_${answer.weekStart}_to_${answer.weekEnd}.csv`, CSV_UTF8_BOM + buildCsv(headers, csvRows));
  };

  // The pop-up's line under the name: the day or the week, then what the row counts there,
  // worked out from the visits listed (each day's distinct stores is its number in the row).
  const visitsWeek = visitsAsk !== null && visitsAsk.from !== visitsAsk.to;
  const visitsSubtitle = (): string => {
    if (!visitsAsk) return '';
    const when = visitsWeek ? shortWeekLabel(visitsAsk.from) : dayLong(visitsAsk.from);
    if (visitsLoading || visitsError) return when;
    const counted = visits.filter((v) => v.counted !== false);
    const stores = new Set(counted.map((v) => v.storeId)).size;
    const days = new Set(counted.map((v) => v.date)).size;
    // Every sign-in not yet signed out, counted or not: one back at a store already counted
    // that day is counted, and is still open all the same.
    const open = visits.filter((v) => v.checkInTime && !v.checkOutTime).length;
    return [
      when,
      `${stores} ${stores === 1 ? 'store' : 'stores'} visited${visitsWeek ? ` on ${days} ${days === 1 ? 'day' : 'days'}` : ''}`,
      ...(open > 0 ? [`${open} still signed in`] : []),
    ].join(' · ');
  };

  if (!projectId) {
    return <div className="p-6 text-sm text-gray-500">Pick a project to see its store visit compliance.</div>;
  }

  const countOf = (status: ShownStatus) => listed.filter((p) => statusOf(p) === status).length;
  const excused = countOf('excused');
  // The week that contains today: only the days already over are judged.
  const running = answer?.weekState === 'current';
  const tiles = [
    { key: 'people', label: 'People', value: listed.length, tone: 'text-gray-900' },
    { key: 'met', label: 'Met target', value: countOf('met'), tone: 'text-green-700' },
    // A finished week has nobody who can still make it, so the tile would only ever say 0.
    ...(running
      ? [{ key: 'in_progress', label: 'Can still make it', value: countOf('in_progress'), tone: 'text-gray-700' }]
      : []),
    { key: 'short', label: 'Short', value: countOf('short'), tone: 'text-red-700' },
    // Most weeks nobody is excused, and a tile that says 0 is one more thing to read.
    ...(excused > 0 ? [{ key: 'excused', label: 'Excused', value: excused, tone: 'text-gray-500' }] : []),
  ];

  return (
    <ReportPageScaffold
      embedded
      title="Compliance"
      searchValue={searchQuery}
      onSearchChange={(e) => setSearchQuery(e.target.value)}
      searchPlaceholder="Search by name or employee code…"
      searchInputPaddingRight="pr-4"
      filters={
        <>
          {roleOptions.length > 0 && (
            <select
              value={roleFilter}
              onChange={(e) => setRoleFilter(e.target.value)}
              aria-label="Role"
              className={`rounded-md border px-3 py-2 text-sm font-medium shadow-sm ${
                roleFilter !== 'all' ? 'border-primary-200 bg-primary-50 text-primary-700' : 'border-gray-300 bg-white text-gray-600 hover:bg-gray-50'
              }`}
            >
              <option value="all">All roles</option>
              {roleOptions.map((o) => <option key={o.value} value={o.value}>{o.label}</option>)}
            </select>
          )}
          <div className="flex items-center gap-1">
            <button
              type="button"
              aria-label="Previous week"
              onClick={() => { if (shownWeek) setWeekStart(shiftDay(shownWeek, -7)); }}
              disabled={!canGoBack}
              className={STEP_BUTTON}
            >
              <ChevronLeft className="h-4 w-4" />
            </button>
            <span aria-live="polite" className="min-w-[10rem] whitespace-nowrap text-center text-sm font-medium text-gray-700">
              {shownWeek ? weekLabel(shownWeek) : ''}
            </span>
            <button
              type="button"
              aria-label="Next week"
              onClick={() => { if (shownWeek) setWeekStart(shiftDay(shownWeek, 7)); }}
              disabled={!canGoForward}
              className={STEP_BUTTON}
            >
              <ChevronRight className="h-4 w-4" />
            </button>
          </div>
          <button
            type="button"
            onClick={handleExport}
            disabled={loading || sort.sorted.length === 0}
            className="flex items-center gap-2 rounded-md border border-gray-300 bg-white px-4 py-2 text-sm font-medium text-gray-700 hover:bg-gray-50 disabled:cursor-not-allowed disabled:opacity-50"
          >
            <Download className="h-4 w-4" /> Download
          </button>
        </>
      }
    >
      {error && <div className="mt-4 rounded-lg border border-red-200 bg-red-50 p-3 text-sm text-red-700">{error}</div>}

      {loading ? (
        <BlockLoader className="py-16" />
      ) : !answer ? null : !answer.targetsSet ? (
        <div className="py-16 text-center text-sm text-gray-500">
          No visit targets were set for this week.
          {configPage && (
            <>
              {' '}
              <Link to="/master-management/store-visit-forms" className="font-medium text-primary-600 hover:text-primary-700">
                Set targets
              </Link>
            </>
          )}
        </div>
      ) : answer.people.length === 0 ? (
        <div className="py-16 text-center text-sm text-gray-500">No one holds a role with visit targets.</div>
      ) : (
        <>
          {!answer.rosterChecked && (
            <p className="mt-3 text-xs text-amber-700">This list may include people who have left.</p>
          )}
          {/* Only an answer that says so: a server from before leave was read says nothing. */}
          {answer.daysOffChecked === false && (
            <p className={`${answer.rosterChecked ? 'mt-3' : 'mt-1'} text-xs text-amber-700`}>
              Leave and week off could not be read for this week.
            </p>
          )}

          {/* A fifth tile gets its own column rather than a row to itself. */}
          <div className={`mt-4 grid grid-cols-2 gap-3 ${tiles.length > 4 ? 'lg:grid-cols-5' : 'lg:grid-cols-4'}`}>
            {tiles.map((t) => (
              <div key={t.key} className="rounded-lg border border-gray-200 p-4">
                <div className={`text-2xl font-semibold tabular-nums ${t.tone}`}>{t.value.toLocaleString()}</div>
                <div className="mt-1 text-xs font-medium text-gray-500">{t.label}</div>
              </div>
            ))}
          </div>

          {listed.length === 0 ? (
            <div className="py-16 text-center text-sm text-gray-500">No one matches these filters.</div>
          ) : (
            <>
              <div className="mt-4 overflow-x-auto">
                <table className="min-w-full divide-y divide-gray-200 text-sm">
                  <thead className="bg-gray-50/70 text-left text-xs font-semibold text-gray-500">
                    <tr>
                      <SortTh label="Person" sortKey="person" sort={sort} className="whitespace-nowrap px-4 py-3" />
                      <SortTh label="Role" sortKey="role" sort={sort} className="whitespace-nowrap px-4 py-3" />
                      {answer.days.map((day) => (
                        <th key={day} className="whitespace-nowrap px-2 py-3 text-center">{dayHead(day)}</th>
                      ))}
                      <SortTh label="Days met" sortKey="daysMet" sort={sort} className="whitespace-nowrap px-4 py-3" />
                      <SortTh label="Status" sortKey="status" sort={sort} className="whitespace-nowrap px-4 py-3" />
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-gray-50 bg-white">
                    {paginatedData.map((p) => (
                      <tr key={p.employeeCode} className="hover:bg-gray-50">
                        <td className="px-4 py-3">
                          <div className="text-gray-900">{nameOf(p)}</div>
                          <div className="text-xs text-gray-400">{p.employeeCode}</div>
                        </td>
                        <td className="px-4 py-3">
                          <div className="text-gray-900">{p.roleName}</div>
                          <div className="text-xs text-gray-400">{storesLine(p)}</div>
                        </td>
                        {answer.days.map((day, i) => {
                          const stores = p.storesByDay[i] ?? 0;
                          const off = offOn(p, i);
                          const upcoming = day > answer.today;
                          const isToday = day === answer.today;
                          const target = targetOn(p, i);
                          // A count above 0 opens the visits it counts; 0 and the days to come open nothing.
                          const count = (tone: string, title?: string) => (
                            <button
                              type="button"
                              title={title}
                              aria-label={`See ${stores} ${stores === 1 ? 'store' : 'stores'} visited ${isToday ? 'so far today' : `on ${dayLong(day)}`}`}
                              onClick={() => setVisitsAsk({ person: p, from: day, to: day })}
                              className={`${DAY_PILL} ${tone} ${PILL_BUTTON}`}
                            >
                              {stores}
                            </button>
                          );
                          return (
                            <td
                              key={day}
                              title={upcoming ? UPCOMING_HINT : undefined}
                              aria-label={upcoming && !off ? UPCOMING_HINT : undefined}
                              className={`px-2 py-3 text-center ${upcoming ? UPCOMING_CELL : ''}`}
                            >
                              {/* Today shows its visits so far in grey, judged once the day is over;
                                  a day off with none yet says which kind. Before today a visit
                                  always shows its count, green or amber against that day's target
                                  (grey on a day that had none), whatever kind of day it was made
                                  on; without one, a day off says which kind. A day still to come
                                  is shaded and shows only leave applied for ahead, never "no visit". */}
                              {isToday ? (
                                stores === 0 && off ? (
                                  <span title={off.title} className={OFF_MARK}>{off.mark}</span>
                                ) : stores === 0 ? (
                                  <span title="Today so far" className={`${DAY_PILL} ${PLAIN_PILL}`}>{stores}</span>
                                ) : (
                                  count(PLAIN_PILL, 'Today so far')
                                )
                              ) : stores > 0 && !upcoming ? (
                                target === null ? (
                                  count(PLAIN_PILL, 'No target this day')
                                ) : (
                                  count(stores >= target ? MET_PILL : BELOW_PILL)
                                )
                              ) : off ? (
                                <span title={off.title} className={OFF_MARK}>{off.mark}</span>
                              ) : upcoming ? null : (
                                <span title={target === null ? 'No target this day' : undefined} className="text-gray-400">–</span>
                              )}
                            </td>
                          );
                        })}
                        {/* Against what was required of this person, not the role's target. Opens
                            the week's visits once there is one. */}
                        <td title={lessRequiredTitle(p)} className="whitespace-nowrap px-4 py-3 tabular-nums text-gray-700">
                          {p.daysVisited > 0 ? (
                            <button
                              type="button"
                              // The figure first, as on screen, so it is part of the button's name.
                              aria-label={`${p.daysMet} / ${requiredOf(p)} days met. See ${nameOf(p)}'s visits for ${shortWeekLabel(answer.weekStart)}`}
                              onClick={() => setVisitsAsk({ person: p, from: answer.days[0], to: answer.days[6] })}
                              className="rounded tabular-nums transition hover:text-primary-700 hover:underline focus:outline-none focus-visible:ring-2 focus-visible:ring-primary-500 focus-visible:ring-offset-1"
                            >
                              {p.daysMet} / {requiredOf(p)}
                            </button>
                          ) : (
                            <>{p.daysMet} / {requiredOf(p)}</>
                          )}
                        </td>
                        <td className="whitespace-nowrap px-4 py-3">
                          <span className={`inline-block whitespace-nowrap rounded px-2 py-0.5 text-xs font-medium ${STATUS[statusOf(p)].chip}`}>
                            {statusLabel(p)}
                          </span>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>

              {/* The People tile already says how many are listed, so this is only the pager. */}
              {totalPages > 1 && (
                <div className="flex items-center justify-end gap-2 border-t border-gray-100 px-4 py-3 text-sm text-gray-600">
                  <button type="button" onClick={() => setCurrentPage((n) => Math.max(1, n - 1))} disabled={safePage <= 1} className="rounded-md border border-gray-300 px-3 py-1.5 disabled:opacity-40">Previous</button>
                  <span>Page {safePage} of {totalPages}</span>
                  <button type="button" onClick={() => setCurrentPage((n) => Math.min(totalPages, n + 1))} disabled={safePage >= totalPages} className="rounded-md border border-gray-300 px-3 py-1.5 disabled:opacity-40">Next</button>
                </div>
              )}

              <div className="mt-3 flex flex-wrap items-center gap-x-4 gap-y-1 text-xs text-gray-500">
                <span className="flex items-center gap-1.5">
                  <span aria-hidden="true" className={`inline-block h-3 w-5 rounded-full ring-1 ring-inset ring-green-200 ${MET_PILL}`} />
                  target met
                </span>
                <span className="flex items-center gap-1.5">
                  <span aria-hidden="true" className={`inline-block h-3 w-5 rounded-full ring-1 ring-inset ring-amber-200 ${BELOW_PILL}`} />
                  below target
                </span>
                {running && (
                  <span className="flex items-center gap-1.5">
                    <span aria-hidden="true" className={`inline-block h-3 w-5 rounded-full ring-1 ring-inset ring-gray-200 ${PLAIN_PILL}`} />
                    today, so far
                  </span>
                )}
                {answer.days.some((day) => day > answer.today) && (
                  <span className="flex items-center gap-1.5">
                    <span aria-hidden="true" className={`inline-block h-3 w-5 rounded-sm ring-1 ring-inset ring-gray-200 ${UPCOMING_CELL}`} />
                    still to come
                  </span>
                )}
                <span className="flex items-center gap-1.5">
                  <span aria-hidden="true" className="text-gray-400">–</span>
                  no visit
                </span>
                <span>
                  <span className="font-medium">L</span> leave · <span className="font-medium">WO</span> week off · <span className="font-medium">H</span> holiday
                </span>
              </div>
            </>
          )}
        </>
      )}

      {/* The Store Visits report's pop-up, with only its Visits list (see the note at the top). */}
      <VisitsModal
        open={visitsAsk !== null}
        mode="manager"
        title={visitsAsk ? nameOf(visitsAsk.person) : ''}
        subtitle={visitsSubtitle()}
        sessions={visits}
        rangeLabel={visitsAsk ? (visitsWeek ? `${visitsAsk.from} to ${visitsAsk.to}` : visitsAsk.from) : ''}
        fileKey={visitsAsk?.person.employeeCode ?? ''}
        loading={visitsLoading}
        error={visitsError}
        onClose={() => setVisitsAsk(null)}
      />
    </ReportPageScaffold>
  );
}
